/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import type { ComponentPropsWithoutRef, ComponentRef } from "react";
import * as React from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  constantSemanticColors,
  radius,
  spacing,
  stroke,
} from "../../../../../global/emotion";

const rootStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.secondary,
  border: `${stroke.regular} solid ${theme.semanticColors.neutralsLow}`,
  borderColor: theme.semanticColors.neutralsLow,
  borderRadius: radius.xs,
  cursor: "pointer",
  display: "flex",
  height: spacing.l,
  justifyContent: "center",
  opacity: 1,
  outline: "none",
  padding: spacing.none,
  position: "relative",
  transition: "all 0.2s ease",
  width: spacing.l,
  ":disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
  ":focus-visible": {
    outline: `2px solid ${theme.semanticColors.accentLow}`,
    outlineOffset: "2px",
  },
  ":is([data-state='checked'])": {
    backgroundColor: theme.semanticColors.accentMedium,
    borderColor: theme.semanticColors.accentMedium,
  },
  ":is([data-state='indeterminate'])": {
    backgroundColor: theme.semanticColors.accentMedium,
    borderColor: theme.semanticColors.accentMedium,
  },
});

const indicatorStyle: Interpolation = {
  alignItems: "center",
  color: constantSemanticColors.onColorHighest,
  display: "flex",
  height: "100%",
  justifyContent: "center",
  transition: "none",
  width: "100%",
};

export type CheckboxProps = ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
>;

export const Checkbox = React.forwardRef<
  ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ ...props }, ref) => (
  <CheckboxPrimitive.Root ref={ref} css={rootStyle} {...props}>
    <CheckboxPrimitive.Indicator css={indicatorStyle} asChild>
      <svg
        width="36"
        height="36"
        viewBox="0 0 36 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M27.4142 15.4142C28.1951 14.6332 28.1951 13.3668 27.4142 12.5858C26.6332 11.8047 25.3668 11.8047 24.5858 12.5858L16 21.1716L11.4142 16.5858C10.6332 15.8047 9.36683 15.8047 8.58579 16.5858C7.80474 17.3668 7.80474 18.6332 8.58579 19.4142L14.5858 25.4142C15.3668 26.1953 16.6332 26.1953 17.4142 25.4142L27.4142 15.4142Z"
          fill="currentColor"
        />
      </svg>
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));

Checkbox.displayName = "Checkbox";
